import { AlertTriangle } from 'lucide-react'
import { use, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { UNSAFE_NavigationContext, useLocation } from 'react-router'

import { Boton, Modal } from '../componentes/ui'
import {
  decidir,
  destinoDelClic,
  envolverNavegador,
  escucharPregunta,
  guardarLosCambios,
  hayCambiosSinGuardar,
  hayQuePreguntar,
  pasosDelHistorial,
  preguntaEnCurso,
  yaSeSalio,
  type Navegador,
} from './cambiosSinGuardar'

/**
 * La guarda de «Tienes cambios sin guardar» para **toda** navegación dentro del panel,
 * sin tocar el enrutador (auditoría del 29-09-2026, P1-20). El editor la monta; mientras
 * haya algo sin guardar (`cambiosSinGuardar.ts`), para:
 *
 * 1. **los clics en enlaces internos**, en fase de captura en el documento: llegan aquí
 *    antes que al `<Link>` del enrutador, así que el menú, la miga de pan o cualquier
 *    enlace preguntan antes de moverse;
 * 2. **las navegaciones por código** (`navigate()`: el selector de webs, un botón que
 *    lleva a otra pantalla), envolviendo el `navigator` del enrutador;
 * 3. **«atrás» y «adelante» del navegador**: el oyente de `popstate` va **antes** que el del
 *    enrutador (ver `alVolverActual`); se deshace el paso sin que él se entere y, si se sale,
 *    se rehace.
 *
 * Recargar o cerrar la pestaña lo avisa el propio navegador (`beforeunload`, en el editor).
 *
 * El diálogo ofrece guardar (si sale bien, sigue), salir sin guardar o quedarse.
 */

/**
 * El `popstate` llega a la ventana, y ahí los navegadores llaman a los oyentes **en el orden
 * en que se registraron**, con captura o sin ella (comprobado en Chromium el 29-09-2026: la
 * captura no adelanta a nadie en el propio destino). El enrutador registra el suyo al
 * montarse; este se registra **al cargarse el módulo**, que es antes —el editor va en el
 * paquete principal—, y así puede parar «atrás» antes de que el enrutador cambie de página.
 * Lo que hace cada vez lo pone la guarda montada; sin ella, nada.
 */
let alVolverActual: null | ((e: PopStateEvent) => void) = null
if (typeof window !== 'undefined') window.addEventListener('popstate', (e) => alVolverActual?.(e), true)

export const GuardaDeNavegacion = () => {
  const { navigator } = use(UNSAFE_NavigationContext)
  const ubicacion = useLocation()
  const actual = useRef('')
  actual.current = `${ubicacion.pathname}${ubicacion.search}${ubicacion.hash}`
  const posicion = useRef<null | number>(null)

  // Tras cada navegación: dónde está el historial, y la guarda vuelve a mirar.
  useEffect(() => {
    const idx = (window.history.state as null | { idx?: unknown })?.idx
    posicion.current = typeof idx === 'number' ? idx : null
    yaSeSalio()
  }, [ubicacion.key])

  useEffect(() => {
    const preguntarYSeguir = (destino: string, seguir: () => void) => void decidir(destino, seguir)
    const soltarNavegador = envolverNavegador(navigator as unknown as Navegador, { actual: () => actual.current, preguntarYSeguir })

    const alClic = (e: MouseEvent) => {
      if (!hayCambiosSinGuardar()) return
      const enlace = (e.target instanceof Element ? e.target.closest('a[href]') : null) as HTMLAnchorElement | null
      if (!enlace) return
      const destino = destinoDelClic(
        { href: enlace.href, target: enlace.target, descarga: enlace.hasAttribute('download'), boton: e.button, teclas: { ctrl: e.ctrlKey, meta: e.metaKey, shift: e.shiftKey, alt: e.altKey }, cancelado: e.defaultPrevented },
        window.location.origin,
      )
      if (destino === null || !hayQuePreguntar(actual.current, destino)) return
      // Ni el navegador ni el `<Link>` del enrutador llegan a ver el clic.
      e.preventDefault()
      e.stopPropagation()
      preguntarYSeguir(destino, () => navigator.push(destino))
    }

    let deshaciendo = false
    const alVolver = (e: PopStateEvent) => {
      if (deshaciendo) {
        deshaciendo = false
        e.stopImmediatePropagation()
        return
      }
      if (!hayCambiosSinGuardar()) return
      const pasos = pasosDelHistorial(posicion.current, e.state)
      if (pasos === 0) return
      // El enrutador no llega a enterarse: se vuelve a donde estábamos y se pregunta.
      e.stopImmediatePropagation()
      deshaciendo = true
      window.history.go(-pasos)
      preguntarYSeguir('', () => window.history.go(pasos))
    }

    document.addEventListener('click', alClic, true)
    alVolverActual = alVolver
    return () => {
      soltarNavegador()
      document.removeEventListener('click', alClic, true)
      if (alVolverActual === alVolver) alVolverActual = null
      // Una pregunta que se queda sin editor detrás no lleva a ninguna parte.
      preguntaEnCurso()?.responder('quedarme')
    }
  }, [navigator])

  return <DialogoDeCambios />
}

const DialogoDeCambios = () => {
  const pregunta = useSyncExternalStore(escucharPregunta, preguntaEnCurso)
  const [guardando, setGuardando] = useState(false)
  if (!pregunta) return null

  const guardarYSalir = async () => {
    setGuardando(true)
    try {
      // Si no se puede guardar, el editor ya enseña por qué junto a cada campo: se queda.
      pregunta.responder((await guardarLosCambios()) ? 'salir' : 'quedarme')
    } finally {
      setGuardando(false)
    }
  }

  return (
    <Modal
      ancho="max-w-md"
      cerrar={() => pregunta.responder('quedarme')}
      pie={
        <>
          <Boton disabled={guardando} onClick={() => pregunta.responder('quedarme')} tipo="discreto">
            Quedarme
          </Boton>
          <Boton disabled={guardando} onClick={() => pregunta.responder('salir')} tipo="peligro">
            Salir sin guardar
          </Boton>
          <Boton cargando={guardando} onClick={() => void guardarYSalir()} tipo="primario">
            Guardar
          </Boton>
        </>
      }
      titulo="Tienes cambios sin guardar"
    >
      <p className="flex items-start gap-2.5 text-sm text-tinta-2">
        <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0 text-aviso" />
        Si sales ahora, lo que has cambiado desde la última vez que guardaste se pierde.
      </p>
    </Modal>
  )
}
